import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Palette } from "lucide-react";
import { WhatsAppIcon } from "@/components/ui/SocialIcons";

interface FloatingAboutDockProps {
  onOpenThemeDialog: () => void;
}

const WHATSAPP_HREF =
  "https://wa.me/918307562996?text=Hello%20!%20I'm%20interested%20in%20Metefy%20QR%20stickers.";

const whatsappBtnClass =
  "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg shadow-lg transition-all duration-300 bg-green-500 hover:bg-green-600 text-white hover:ring-2 hover:ring-green-400/50 ring-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

/** Theme button (scroll-hide) on top; WhatsApp always visible below — opens WhatsApp directly. */
export function FloatingAboutDock({ onOpenThemeDialog }: FloatingAboutDockProps) {
  const [mounted, setMounted] = useState(false);
  const [themeVisible, setThemeVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY < 10) {
        setThemeVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setThemeVisible(false);
      } else if (currentScrollY < lastScrollY) {
        setThemeVisible(true);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const dock = (
    <div
      className="fixed bottom-20 right-4 z-40 flex flex-col items-end gap-2"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          transition: "opacity 0.3s, transform 0.3s",
          opacity: themeVisible ? 1 : 0,
          transform: themeVisible ? "translateY(0)" : "translateY(20px)",
          pointerEvents: themeVisible ? "auto" : "none",
        }}
      >
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onOpenThemeDialog();
          }}
          variant="ghost"
          size="icon"
          className="h-10 w-10 bg-background/90 backdrop-blur-sm border border-border/80 shadow-lg hover:bg-background hover:border-border transition-all duration-300 rounded-lg"
          aria-label="Open theme settings"
        >
          <Palette className="h-5 w-5 text-foreground" />
        </Button>
      </div>

      <a
        href={WHATSAPP_HREF}
        target="_blank"
        rel="noopener noreferrer"
        className={whatsappBtnClass}
        aria-label="Contact us on WhatsApp"
      >
        <WhatsAppIcon className="h-5 w-5" />
      </a>
    </div>
  );

  if (mounted) {
    return createPortal(dock, document.body);
  }
  return null;
}
